import React, { useState } from 'react';
import { BookOpen, Code2, Webhook, Shield, Database, Mail, Download, Copy, Check, ChevronRight, AlertTriangle } from 'lucide-react';
import { API } from '../lib/api';


export default function IntegrationDocsPage() {
  const [tab, setTab] = useState('signup');
  const [copied, setCopied] = useState('');

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: 1100,
      margin: '0 auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <BookOpen size={22} color="#3b82f6" />
        <div>
          <h1 style={{ color: 'var(--text-primary, #1e293b)', fontSize: 22, margin: 0, fontWeight: 800 }}>Integration Documentation</h1>
          <p style={{ color: 'var(--text-muted, #64748b)', fontSize: 12, margin: '4px 0 0' }}>
            Complete guides for integrating RetailCLIQ ERP into external projects
          </p>
        </div>
        <a
          href={`${API}/security/download-integration`}
          style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, background: 'rgba(59,130,246,0.18)', color: '#60a5fa', border: '1px solid #3b82f6', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
          data-testid="download-integration-zip"
        >
          <Download size={13} /> Download Integration ZIP
        </a>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: '1px solid rgba(148,163,184,0.2)', paddingBottom: 0 }}>
        {[
          { k: 'signup',  label: 'Signup Project',   icon: <Code2 size={14} /> },
          { k: 'erp',     label: 'ERP Backend',       icon: <Shield size={14} /> },
          { k: 'webhook', label: 'Webhook Reference', icon: <Webhook size={14} /> },
          { k: 'files',   label: 'File Manifest',     icon: <Database size={14} /> },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`integration-tab-${t.k}`}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: '8px 8px 0 0',
              background: tab === t.k ? 'rgba(59,130,246,0.18)' : 'transparent',
              color: tab === t.k ? '#60a5fa' : 'var(--text-muted, #64748b)',
              borderBottom: tab === t.k ? '2px solid #3b82f6' : '2px solid transparent'
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'signup' && <SignupGuide copy={copy} copied={copied} />}
      {tab === 'erp'    && <ErpGuide    copy={copy} copied={copied} />}
      {tab === 'webhook'&& <WebhookGuide copy={copy} copied={copied} />}
      {tab === 'files'  && <FileManifest />}
    </div>
  );
}

// ── Shared components ───────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ color: 'var(--text-primary, #1e293b)', fontSize: 14, fontWeight: 700, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <ChevronRight size={14} color="#3b82f6" /> {title}
      </h3>
      {children}
    </div>
  );
}

function CodeBlock({ code, id, copy, copied }) {
  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <pre style={{
        background: '#0f172a', color: '#e2e8f0', fontSize: 11, fontFamily: 'Source Code Pro, monospace',
        padding: '16px 44px 16px 16px', borderRadius: 8, border: '1px solid rgba(148,163,184,0.2)',
        overflowX: 'auto', margin: 0, lineHeight: 1.6, maxHeight: 360, overflowY: 'auto'
      }}>
        <code>{code}</code>
      </pre>
      {copy && (
        <button
          onClick={() => copy(code, id)}
          style={{ position: 'absolute', top: 8, right: 8, padding: '4px 8px', borderRadius: 4, background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 10 }}
        >
          {copied === id ? <Check size={12} /> : <Copy size={12} />}
        </button>
      )}
    </div>
  );
}

function Badge({ color = 'blue', children }) {
  const colors = {
    blue:   { bg: 'rgba(59,130,246,0.15)',  text: '#60a5fa',  border: '#3b82f6' },
    green:  { bg: 'rgba(16,185,129,0.15)',  text: '#10b981',  border: '#10b981' },
    amber:  { bg: 'rgba(245,158,11,0.15)',  text: '#fbbf24',  border: '#f59e0b' },
    red:    { bg: 'rgba(239,68,68,0.15)',   text: '#f87171',  border: '#ef4444' },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {children}
    </span>
  );
}

// ── Signup Guide ────────────────────────────────────────────────────────────
function SignupGuide({ copy, copied }) {
  return (
    <div>
      <div style={{ padding: 14, borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1.5px solid rgba(16,185,129,0.5)', color: 'var(--text-primary, #1e293b)', fontSize: 13, marginBottom: 20, display: 'flex', gap: 10 }}>
        <Shield size={15} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
        <span>The <strong>Signup Project</strong> is a separate HTML/JS or React page that lets new customers self-provision their ERP tenant. It calls the public API below — no authentication required.</span>
      </div>

      <Section title="API Endpoint Sequence">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { method: 'GET',  path: '/api/signup/pricing-tiers', desc: 'Load tier cards for landing page' },
            { method: 'GET',  path: '/api/signup/industries',    desc: 'Load industry selector' },
            { method: 'POST', path: '/api/signup/initiate',      desc: 'Create pending application → get applicationId' },
            { method: 'POST', path: '/api/signup/otp/send',      desc: 'Send OTP to email & phone' },
            { method: 'POST', path: '/api/signup/otp/verify',    desc: 'Verify 6-digit codes' },
            { method: 'POST', path: '/api/signup/mock-payment/verify', desc: '(Paid tiers only) Verify payment' },
            { method: 'POST', path: '/api/signup/complete',      desc: 'Provision tenant + issue license + send welcome email' },
          ].map(({ method, path, desc }) => (
            <div key={path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 6, background: 'var(--surface-2, #f1f5f9)', border: '1px solid var(--border, #e2e8f0)' }}>
              <Badge color={method === 'GET' ? 'green' : 'blue'}>{method}</Badge>
              <code style={{ color: 'var(--text-primary, #1e293b)', fontSize: 13, fontFamily: 'monospace', fontWeight: 600, flex: 1 }}>{path}</code>
              <span style={{ color: 'var(--text-secondary, #475569)', fontSize: 12 }}>{desc}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Initiate Request Body">
        <CodeBlock id="initiate-body" copy={copy} copied={copied} code={`POST /api/signup/initiate
{
  "companyName":   "Acme Apparels Pvt Ltd",  // required
  "tradeName":     "Acme",                    // optional
  "tierCode":      "Lite",                    // required: Lite|Express|Standard|Professional|Enterprise
  "industryCode":  "Apparel",                 // required: from /industries
  "adminName":     "John Doe",               // required
  "adminEmail":    "admin@acme.com",          // required — must be unique
  "adminPhone":    "9999999999",             // optional
  "country":       "IN",                     // optional, default IN
  "expectedUsers": 5                          // optional
}`} />
      </Section>

      <Section title="OTP Verification">
        <div style={{ padding: 12, borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1.5px solid rgba(245,158,11,0.5)', color: 'var(--text-primary, #1e293b)', fontSize: 13, marginBottom: 10 }}>
          <strong>Dev Mode:</strong> When SMTP is not configured, <code style={{ background: 'rgba(0,0,0,0.07)', padding: '1px 5px', borderRadius: 3 }}>devEmailOtp</code> is returned in the send response. When Twilio is not configured, <code style={{ background: 'rgba(0,0,0,0.07)', padding: '1px 5px', borderRadius: 3 }}>devPhoneOtp</code> is returned. Use these for testing only.
        </div>
        <CodeBlock id="otp-flow" copy={copy} copied={copied} code={`// 1. Send OTPs
POST /api/signup/otp/send
{ "applicationId": "APP-XXXX" }
// → { ok:true, maskedEmail:"ad***@acme.com", devEmailOtp?:"123456", devPhoneOtp?:"654321" }

// 2. Verify OTPs
POST /api/signup/otp/verify
{ "applicationId": "APP-XXXX", "emailOtp": "123456", "phoneOtp": "654321" }
// → { ok:true, emailVerified:true, phoneVerified:true }`} />
      </Section>

      <Section title="Complete Response (what you get back)">
        <CodeBlock id="complete-response" copy={copy} copied={copied} code={`{
  "success": true,
  "clientId": "IN_LIT_1234",
  "tier": "Lite",
  "tierName": "Lite",
  "databaseName": "RC26ERP_APPAREL_001",
  "logins": {
    "licensee":     { "username": "admin1234",   "password": "Abc123xyz" },
    "manager":      { "username": "manager1234", "password": "Def456uvw" },
    "adminSupport": { "username": "ssadmin1234", "password": "Ghi789rst" },
    "licensor":     { "username": "SysCLIQ1234", "password": "Ghi789rst" }
  }
}`} />
      </Section>

      <Section title="Error Reference">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border, #e2e8f0)', background: 'var(--surface-2, #f8fafc)' }}>
              {['Code','HTTP','Description'].map(h => <th key={h} style={{ padding: '10px 12px', color: 'var(--text-primary, #1e293b)', textAlign: 'left', fontWeight: 700 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {[
              ['EMAIL_NOT_UNIQUE','409','Email already registered'],
              ['EMAIL_NOT_VERIFIED','400','OTP step was skipped before /complete'],
              ['OTP_EXPIRED','400','OTP timed out (10 min window)'],
              ['INVALID_OTP','400','Wrong code entered'],
              ['PAYMENT_REQUIRED','402','Paid tier without verified payment'],
              ['APPLICATION_NOT_FOUND','404','applicationId not found in session'],
              ['DB_NOT_AVAILABLE','503','MSSQL not connected'],
            ].map(([code, http, desc]) => (
              <tr key={code} style={{ borderBottom: '1px solid var(--border, #e2e8f0)' }}>
                <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#dc2626', fontSize: 12, fontWeight: 700 }}>{code}</td>
                <td style={{ padding: '9px 12px', color: '#b45309', fontWeight: 700, fontSize: 13 }}>{http}</td>
                <td style={{ padding: '9px 12px', color: 'var(--text-primary, #1e293b)', fontSize: 13 }}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

// ── ERP Backend Guide ────────────────────────────────────────────────────────
function ErpGuide({ copy, copied }) {
  return (
    <div>
      <Section title="Quick Mount (server.js)">
        <CodeBlock id="mount-code" copy={copy} copied={copied} code={`'use strict';
const express = require('express');
const app = express();
app.use(express.json({ verify: (req,_,buf) => { req.rawBody = buf.toString('utf8'); } }));

const ctx = require('./routes/_context');
require('./routes/auth').mount(app, ctx);
require('./routes/signup').mount(app, ctx);
require('./routes/webhook').mount(app, ctx);
require('./routes/license').mount(app, ctx);
require('./routes/uams').mount(app, ctx);
require('./routes/security-config').mount(app, ctx);
require('./routes/health').mount(app, ctx);

app.listen(8001, () => console.log('ERP Security running on :8001'));`} />
      </Section>

      <Section title="Required Environment Variables">
        <CodeBlock id="env-vars" copy={copy} copied={copied} code={`MSSQL_SERVER=your-sql-server
MSSQL_PORT=1433
MSSQL_USER=sa
MSSQL_PASSWORD=your-password
MSSQL_DATABASE=RC26MASTERDB
JWT_SECRET=your-strong-jwt-secret
SMTP_HOST=smtp.gmail.com
SMTP_PORT=587
SMTP_USER=license@yourdomain.com
SMTP_PASS=your-app-password
FROM_EMAIL=license@yourdomain.com
WEBHOOK_HMAC_SECRET=your-32-char-random-secret
LICENSOR_MASTER_CLIENT_ID=SYS_LIC_0001`} />
      </Section>

      <Section title="4 Default Users Provisioned Per Signup">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border, #e2e8f0)', background: 'var(--surface-2, #f8fafc)' }}>
              {['Role','Username Pattern','UserType','Access Level'].map(h => <th key={h} style={{ padding: '10px 12px', color: 'var(--text-primary, #1e293b)', textAlign: 'left', fontWeight: 700 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {[
              ['Admin (Licensee)','admin<suffix>','LICENSEE','Full ERP access'],
              ['Manager','manager<suffix>','MANAGER','Standard ERP access'],
              ['Licensor (SysCLIQ)','SysCLIQ<suffix>','LICENSOR','License + system management'],
              ['Support Admin','ssadmin<suffix>','ADMINSUPPORT','Support access'],
            ].map(([role, pattern, type, access]) => (
              <tr key={role} style={{ borderBottom: '1px solid var(--border, #e2e8f0)' }}>
                <td style={{ padding: '9px 12px', color: 'var(--text-primary, #1e293b)', fontWeight: 600, fontSize: 13 }}>{role}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#2563eb', fontSize: 13, fontWeight: 700 }}>{pattern}</td>
                <td style={{ padding: '9px 12px', color: '#059669', fontSize: 12, fontWeight: 700 }}>{type}</td>
                <td style={{ padding: '9px 12px', color: 'var(--text-primary, #1e293b)', fontSize: 13 }}>{access}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 8, padding: 12, borderRadius: 6, background: 'rgba(16,185,129,0.1)', border: '1.5px solid rgba(16,185,129,0.4)', fontSize: 13, color: 'var(--text-primary, #1e293b)' }}>
          <strong>Note:</strong> All 4 users also have corresponding <code style={{ background: 'rgba(0,0,0,0.07)', padding: '1px 5px', borderRadius: 3 }}>UserMaster</code> rows in the tenant DB (<code style={{ background: 'rgba(0,0,0,0.07)', padding: '1px 5px', borderRadius: 3 }}>UA/UM/UL/US + suffix</code>) with tier-appropriate <code style={{ background: 'rgba(0,0,0,0.07)', padding: '1px 5px', borderRadius: 3 }}>UserPrivileges</code> seeded from the template company.
        </div>
      </Section>

      <Section title="Login → JWT Flow">
        <CodeBlock id="auth-flow" copy={copy} copied={copied} code={`// Step 1: Login
POST /api/auth/login
{ "username": "admin1234", "password": "YourPassword" }
// → { token: "eyJ...", clientId: "IN_LIT_1234", role: "LICENSEE", ... }

// Step 2: Use JWT on protected routes
GET /api/license/client/IN_LIT_1234
Authorization: Bearer eyJ...`} />
      </Section>
    </div>
  );
}

// ── Webhook Guide ────────────────────────────────────────────────────────────
function WebhookGuide({ copy, copied }) {
  return (
    <div>
      <div style={{ padding: 14, borderRadius: 8, background: 'rgba(59,130,246,0.1)', border: '1.5px solid rgba(59,130,246,0.4)', color: 'var(--text-primary, #1e293b)', fontSize: 13, marginBottom: 20 }}>
        The webhook is for <strong>backend-to-backend</strong> provisioning. It uses HMAC-SHA256 signing to verify the caller. No OTP required — trust is established cryptographically.
      </div>

      <Section title="Sending a Webhook Request (Node.js)">
        <CodeBlock id="webhook-node" copy={copy} copied={copied} code={`const crypto  = require('crypto');
const payload = {
  applicationId: 'YOUR-APP-001',  // your unique ID
  tierCode:      'Express',
  industryCode:  'Apparel',
  companyName:   'Acme Retail Pvt Ltd',
  adminEmail:    'admin@acme.com',
  adminName:     'John Doe',
  adminPhone:    '9999999999',
  country:       'IN',
  expectedUsers: 10,
  defaultPassword: 'Welcome@123'  // optional — auto-generated if omitted
};

const ts   = Math.floor(Date.now() / 1000);
const body = JSON.stringify(payload);
const sig  = 'sha256=' + crypto
  .createHmac('sha256', process.env.WEBHOOK_SECRET)
  .update(\`\${ts}.\${body}\`)
  .digest('hex');

await fetch('https://your-erp-backend.com/api/webhook/signup', {
  method: 'POST',
  headers: {
    'Content-Type':         'application/json',
    'X-Webhook-Signature':  sig,
    'X-Webhook-Timestamp':  String(ts),
  },
  body
});`} />
      </Section>

      <Section title="Response">
        <CodeBlock id="webhook-response" copy={copy} copied={copied} code={`// Success 200
{
  "ok":        true,
  "clientId":  "IN_EXP_9876",
  "licenseId": "550e8400-e29b-41d4-...",
  "logins": {
    "licensee":     { "username": "admin9876",   "password": "..." },
    "manager":      { "username": "manager9876", "password": "..." },
    "adminSupport": { "username": "ssadmin9876", "password": "..." },
    "licensor":     { "username": "SysCLIQ9876", "password": "..." }
  }
}

// Error 401 — invalid signature
{ "error": "BAD_SIGNATURE", "reason": "STALE_TIMESTAMP" }`} />
      </Section>

      <Section title="Idempotency">
        <p style={{ color: 'var(--text-muted, #64748b)', fontSize: 12 }}>
          Re-sending the same <code style={{ color: '#60a5fa' }}>applicationId</code> returns the cached result without re-provisioning. This makes it safe to retry on network failures.
        </p>
      </Section>
    </div>
  );
}

// ── File Manifest ────────────────────────────────────────────────────────────
function FileManifest() {
  const backendFiles = [
    ['security-node/db.js',                    'MSSQL connection pool',                         'mandatory'],
    ['security-node/auth.js',                  '2-step JWT authentication',                    'mandatory'],
    ['security-node/provision-service.js',     'Tenant provisioning pipeline',                  'mandatory'],
    ['security-node/saas-service.js',          'License issuance',                              'mandatory'],
    ['security-node/uams-service.js',          'UAMS privilege matrix',                         'mandatory'],
    ['security-node/tier-columns.js',          'Tier → column mapping',                         'mandatory'],
    ['security-node/client-id-allocator.js',   'ClientId allocation',                           'mandatory'],
    ['security-node/client-id-suffix.js',      'ClientId suffix parsing',                       'mandatory'],
    ['security-node/live-db-allocator.js',     'Pool/dedicated DB allocation',                  'mandatory'],
    ['security-node/license-engine.js',        'License generation + renewal emails',           'mandatory'],
    ['security-node/webhook-hmac.js',          'Webhook HMAC verification',                     'optional'],
    ['security-node/identity-dna.js',          'Owner DNA Signature',                           'optional'],
    ['security-node/signature-guard.js',       'Tamper detection',                              'optional'],
    ['security-node/hbs-date.js',              'Integer-based date system',                     'mandatory'],
    ['security-node/env-cipher.js',            'Credential encryption',                         'mandatory'],
    ['security-node/usertypes.js',             'User type constants',                           'mandatory'],
    ['security-node/tier-integrity.js',        'Tier-integrity snapshot/verification',          'optional'],
    ['routes/_context.js',                     'Auth middleware factory',                       'mandatory'],
    ['routes/auth.js',                         'Login/logout endpoints',                        'mandatory'],
    ['routes/signup.js',                       'Public signup + OTP endpoints',                 'mandatory'],
    ['routes/webhook.js',                      'HMAC webhook endpoint',                         'optional'],
    ['routes/license.js',                      'License CRUD',                                  'mandatory'],
    ['routes/uams.js',                         'UAMS matrix APIs',                              'optional'],
    ['routes/health.js',                       'Health check endpoint',                         'mandatory'],
  ];

  return (
    <div>
      <div style={{ padding: 12, borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1.5px solid rgba(245,158,11,0.5)', color: 'var(--text-primary, #1e293b)', fontSize: 13, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Use <strong>Key Settings → Download Integration ZIP</strong> to get all files pre-packaged. The table below is a reference manifest.</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border, #e2e8f0)', background: 'var(--surface-2, #f8fafc)' }}>
            {['File','Purpose','Required'].map(h => <th key={h} style={{ padding: '10px 12px', color: 'var(--text-primary, #1e293b)', textAlign: 'left', fontWeight: 700 }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {backendFiles.map(([file, purpose, req]) => (
            <tr key={file} style={{ borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#2563eb', fontSize: 12, fontWeight: 600 }}>{file}</td>
              <td style={{ padding: '9px 12px', color: 'var(--text-primary, #1e293b)', fontSize: 13 }}>{purpose}</td>
              <td style={{ padding: '9px 12px' }}>
                <span style={{ padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: req === 'mandatory' ? 'rgba(220,38,38,0.12)' : 'rgba(100,116,139,0.12)', color: req === 'mandatory' ? '#dc2626' : '#475569', border: `1px solid ${req === 'mandatory' ? '#dc2626' : '#94a3b8'}` }}>
                  {req.toUpperCase()}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
